import { noop } from 'lodash';

import { HealthcareProviderNfzConfiguration } from '@dreryk/egabinet-nfz-configuration-frontend-api';

import { assertUnreachable } from '@dreryk/react-common';

import { XOR } from '~libs.typescript';

import { NfzLoginFormBean, NfzLoginFormController, NfzLoginTypedFormController } from './NfzLoginForm.types';
import { PasswordChangeEnabledFormBeanPart } from './fields/PasswordChangeEnabledField';
import { MfaStrategyFormBeanPart } from './fields/MfaStrategyField';
import { MFA_SECRET_PLACEHOLDER_VALUE, MfaSecretFormBeanPart } from './fields/MfaSecretField';
import { MfaTotpFormBeanPart } from './fields/MfaTotpField';
import { EwusEnabledFormBeanPart } from './fields/EwusEnabledField';
import { EzwmEnabledFormBeanPart } from './fields/EzwmEnabledField';

type ContractingPartyTypeVisitorConfig = {
   nfzConfiguration: HealthcareProviderNfzConfiguration | null;
};

export const contractingPartyTypeVisitor = (config: ContractingPartyTypeVisitorConfig) => <ACCEPT>(visitor: {
   visitPerson?: (data: { personnelCodeRequired: boolean }) => ACCEPT;
   visitProvider?: () => ACCEPT;
}) => {
   if (!config.nfzConfiguration) {
      return noop();
   }

   switch (config.nfzConfiguration.contractingPartyType) {
      case 'PERSON':
         return visitor.visitPerson?.({
            personnelCodeRequired: config.nfzConfiguration.nfzDepartment.identifierRequired,
         });
      case 'PROVIDER':
         return visitor.visitProvider?.();
      default:
         return assertUnreachable(config.nfzConfiguration.contractingPartyType);
   }
};

export const passwordChangeFormPartNotVisible = (
   controller: NfzLoginFormController
): controller is NfzLoginTypedFormController<PasswordChangeEnabledFormBeanPart<false>> => {
   return !controller.watch('passwordChangeEnabled');
};

export const passwordChangeFormPartVisible = (
   controller: NfzLoginFormController
): controller is NfzLoginTypedFormController<PasswordChangeEnabledFormBeanPart<true>> => {
   return controller.watch('passwordChangeEnabled');
};

export const ewusFormPartVisible = (
   controller: NfzLoginFormController
): controller is NfzLoginTypedFormController<EwusEnabledFormBeanPart<true>> => {
   return controller.watch('ewusEnabled');
};

export const ewusFormPartNotVisible = (
   controller: NfzLoginFormController
): controller is NfzLoginTypedFormController<EwusEnabledFormBeanPart<false>> => {
   return !controller.watch('ewusEnabled');
};

export const ezwmFormPartVisible = (
   controller: NfzLoginFormController<EwusEnabledFormBeanPart<false>>
): controller is NfzLoginTypedFormController<EwusEnabledFormBeanPart<false> & EzwmEnabledFormBeanPart<true>> => {
   const values = controller.watch();
   return Boolean(!values.ewusEnabled && values.ezwmEnabled);
};

export const mfaTotpFormPartVisible = (
   controller: NfzLoginFormController<EwusEnabledFormBeanPart<true>>
): controller is NfzLoginTypedFormController<EwusEnabledFormBeanPart<true> & MfaStrategyFormBeanPart<'TOTP'>> => {
   const values = controller.watch();
   return Boolean(values.ewusEnabled && values.mfaStrategy === 'TOTP');
};

export const mfaSecretFormPartVisible = (
   controller: NfzLoginFormController<EwusEnabledFormBeanPart<true>>
): controller is NfzLoginTypedFormController<EwusEnabledFormBeanPart<true> & MfaStrategyFormBeanPart<'SECRET'>> => {
   const values = controller.watch();
   return Boolean(values.ewusEnabled && values.mfaStrategy === 'SECRET');
};

type EzwmMfaInitialValues = XOR<
   [MfaStrategyFormBeanPart<'SECRET'> & MfaSecretFormBeanPart, MfaStrategyFormBeanPart<'TOTP'> & MfaTotpFormBeanPart]
>;

export const getEzwmMfaInitialValues = (mfaSecretPresent: boolean): EzwmMfaInitialValues => {
   if (mfaSecretPresent) {
      return { mfaStrategy: 'SECRET', mfaSecret: MFA_SECRET_PLACEHOLDER_VALUE };
   }

   return { mfaStrategy: 'TOTP', mfaTotp: null };
};

export const ezwmMfaFormPartVisible = (
   controller: NfzLoginFormController
): controller is NfzLoginTypedFormController<EwusEnabledFormBeanPart<false> & EzwmEnabledFormBeanPart<true>> => {
   return ewusFormPartNotVisible(controller) && ezwmFormPartVisible(controller);
};

export const ezwmMfaTotpFormPartVisible = (
   controller: NfzLoginFormController<EwusEnabledFormBeanPart<false> & EzwmEnabledFormBeanPart<true>>
): controller is NfzLoginTypedFormController<
   EwusEnabledFormBeanPart<false> & EzwmEnabledFormBeanPart<true> & MfaStrategyFormBeanPart<'TOTP'>
> => {
   const values = controller.watch();
   return Boolean(!values.ewusEnabled && values.ezwmEnabled && values.mfaStrategy === 'TOTP');
};

export const ezwmMfaSecretFormPartVisible = (
   controller: NfzLoginFormController<EwusEnabledFormBeanPart<false> & EzwmEnabledFormBeanPart<true>>
): controller is NfzLoginTypedFormController<
   EwusEnabledFormBeanPart<false> & EzwmEnabledFormBeanPart<true> & MfaStrategyFormBeanPart<'SECRET'>
> => {
   const values = controller.watch();
   return Boolean(!values.ewusEnabled && values.ezwmEnabled && values.mfaStrategy === 'SECRET');
};

export const ewusNewPasswordSaveActionVisible = (
   controller: NfzLoginFormController
): controller is NfzLoginTypedFormController<
   EwusEnabledFormBeanPart<true> & PasswordChangeEnabledFormBeanPart<true>
> => {
   return ewusFormPartVisible(controller) && passwordChangeFormPartVisible(controller);
};

export const ewusLoginActionVisible = (
   controller: NfzLoginFormController
): controller is NfzLoginTypedFormController<
   EwusEnabledFormBeanPart<true> & PasswordChangeEnabledFormBeanPart<false>
> => {
   return ewusFormPartVisible(controller) && passwordChangeFormPartNotVisible(controller);
};

export const ezwmNewPasswordSaveActionVisible = (
   controller: NfzLoginFormController
): controller is NfzLoginTypedFormController<
   EzwmEnabledFormBeanPart<true> & PasswordChangeEnabledFormBeanPart<true>
> => {
   return (
      ewusFormPartNotVisible(controller) && ezwmFormPartVisible(controller) && passwordChangeFormPartVisible(controller)
   );
};

export const ezwmLoginActionVisible = (
   controller: NfzLoginFormController
): controller is NfzLoginTypedFormController<
   EzwmEnabledFormBeanPart<true> & PasswordChangeEnabledFormBeanPart<false>
> => {
   return (
      passwordChangeFormPartNotVisible(controller) &&
      ewusFormPartNotVisible(controller) &&
      ezwmFormPartVisible(controller)
   );
};

export const mfaStrategyVisitor = (formBean: NfzLoginFormBean<EwusEnabledFormBeanPart<true>>) => <ACCEPT>(visitor: {
   visitTotp?: (data: { mfaAuthorizationCode: string | null }) => ACCEPT;
   visitSecretCode?: (data: { mfaSecret: string | null }) => ACCEPT;
}) => {
   if (!formBean.mfaStrategy) {
      return noop();
   }

   switch (formBean.mfaStrategy) {
      case 'TOTP':
         return visitor.visitTotp?.({
            mfaAuthorizationCode: formBean.mfaTotp,
         });
      case 'SECRET':
         return visitor.visitSecretCode?.({
            mfaSecret: formBean.mfaSecret === MFA_SECRET_PLACEHOLDER_VALUE ? null : formBean.mfaSecret,
         });
      default:
         return assertUnreachable(formBean);
   }
};

export const ezwmMfaStrategyVisitor = (formBean: NfzLoginFormBean<EzwmEnabledFormBeanPart<true>>) => <ACCEPT>(visitor: {
   visitTotp?: (data: { mfaAuthorizationCode: string | null }) => ACCEPT;
   visitSecretCode?: (data: { mfaSecret: string | null }) => ACCEPT;
}) => {
   if (!formBean.mfaStrategy) {
      return noop();
   }

   switch (formBean.mfaStrategy) {
      case 'TOTP':
         return visitor.visitTotp?.({
            mfaAuthorizationCode: formBean.mfaTotp,
         });
      case 'SECRET':
         return visitor.visitSecretCode?.({
            mfaSecret: formBean.mfaSecret === MFA_SECRET_PLACEHOLDER_VALUE ? null : formBean.mfaSecret,
         });
      default:
         return assertUnreachable(formBean);
   }
};
